/*
  Product card colour swatches.
  - Hover/focus a swatch: preview that colour's image.
  - Click/tap a swatch: keep that colour's image on the card (no navigation)
    and point the card's product link at that colour's variant.
*/
if (!customElements.get('card-swatches')) {
  customElements.define(
    'card-swatches',
    class CardSwatches extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.wrapper = this.closest('.card-wrapper') || this.closest('.card');
        if (!this.wrapper) return;
        this.media = this.wrapper.querySelector('.card__media .media');
        this.image = this.media ? this.media.querySelector('img') : null;
        this.links = Array.from(this.wrapper.querySelectorAll('.card__heading a[href]'));
        this.originalHref = this.links.length ? this.links[0].getAttribute('href') : null;

        this.base = this.image
          ? { src: this.image.getAttribute('src'), srcset: this.image.getAttribute('srcset'), selected: false }
          : null;

        this.swatches = Array.from(this.querySelectorAll('.card-swatch'));
        this.swatches.forEach((swatch) => {
          swatch.addEventListener('pointerenter', () => this.preview(swatch));
          swatch.addEventListener('focus', () => this.preview(swatch));
          swatch.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.select(swatch);
          });
        });

        this.onLeave = () => this.reset();
        this.wrapper.addEventListener('mouseleave', this.onLeave);
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.reset();
        });

        this.wrapper.addEventListener('pointerenter', () => this.preload(), { once: true });
      }

      disconnectedCallback() {
        if (this.wrapper && this.onLeave) this.wrapper.removeEventListener('mouseleave', this.onLeave);
      }

      preload() {
        if (!this.image) return;
        const sizes = this.image.getAttribute('sizes');
        this.swatches.forEach((swatch) => {
          if (!swatch.dataset.src) return;
          const img = new Image();
          if (sizes) img.sizes = sizes;
          if (swatch.dataset.srcset) img.srcset = swatch.dataset.srcset.trim();
          img.src = swatch.dataset.src;
        });
      }

      show(src, srcset, swatchActive) {
        if (!this.image || !src) return;
        if (srcset) {
          this.image.setAttribute('srcset', srcset);
        } else {
          this.image.removeAttribute('srcset');
        }
        this.image.setAttribute('src', src);
        this.media.classList.toggle('is-swatch-preview', swatchActive);
      }

      preview(swatch) {
        this.swatches.forEach((s) => s.classList.toggle('is-active', s === swatch));
        if (swatch.dataset.src) {
          this.show(swatch.dataset.src, swatch.dataset.srcset && swatch.dataset.srcset.trim(), true);
        }
      }

      select(swatch) {
        this.swatches.forEach((s) => {
          const selected = s === swatch;
          s.classList.toggle('is-selected', selected);
          s.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        if (swatch.dataset.src && this.image) {
          this.base = {
            src: swatch.dataset.src,
            srcset: swatch.dataset.srcset ? swatch.dataset.srcset.trim() : null,
            selected: true,
          };
          this.show(this.base.src, this.base.srcset, true);
        }

        const href = swatch.dataset.variantUrl || this.originalHref;
        if (href) this.links.forEach((link) => link.setAttribute('href', href));
      }

      reset() {
        this.swatches.forEach((s) => s.classList.remove('is-active'));
        if (this.base) this.show(this.base.src, this.base.srcset, this.base.selected);
      }
    }
  );
}
